import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';

export type Theme = 'light' | 'dark' | 'system' | 'ocean' | 'rose' | 'high-contrast';
const supportedThemes: readonly Theme[] = ['light', 'dark', 'system', 'ocean', 'rose', 'high-contrast'];
const preferenceKey = 'ipm.theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly media = this.document.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
  readonly theme = signal<Theme>(this.savedTheme());
  readonly resolvedTheme = signal<'light' | 'dark'>('light');

  constructor() {
    const listener = () => { if (this.theme() === 'system') this.applyTheme(); };
    this.media?.addEventListener('change', listener);
    this.destroyRef.onDestroy(() => this.media?.removeEventListener('change', listener));
    this.applyTheme();
  }

  setTheme(theme: string): void {
    if (!supportedThemes.includes(theme as Theme)) return;
    this.theme.set(theme as Theme);
    this.applyTheme();
    try { this.document.defaultView?.localStorage.setItem(preferenceKey, theme); } catch {}
  }

  private applyTheme(): void {
    const dark = this.theme() === 'dark' || (this.theme() === 'system' && this.media?.matches === true);
    const resolved = dark ? 'dark' : 'light';
    this.resolvedTheme.set(resolved);
    this.document.documentElement.setAttribute('data-theme', resolved);
    this.document.documentElement.setAttribute('data-palette', this.theme() === 'system' ? resolved : this.theme());
    this.document.documentElement.style.colorScheme = resolved;
  }

  private savedTheme(): Theme {
    try {
      const theme = this.document.defaultView?.localStorage.getItem(preferenceKey);
      return supportedThemes.includes(theme as Theme) ? theme as Theme : 'light';
    } catch { return 'light'; }
  }
}